import { useState, useEffect } from "react";
import { API_ENDPOINTS } from "../../../constants/constants";
import {
  FaEdit,
  FaTrash,
  FaEye,
  FaTimes,
  FaExclamationTriangle,
  FaArrowLeft,
  FaPlus,
  FaVideo,
} from "react-icons/fa";

interface Module {
  id: string;
  title: string;
  description: string;
  createdAt: string;
  isActive: boolean;
  order?: number;
  content?: string;
}

interface Capsule {
  id: string;
  title: string;
  description: string;
  content: string;
  videoUrl?: string;
  createdAt: string;
  moduleId: string;
  time?: number;
  order?: number;
}

interface CreateModuleForm {
  title: string;
  description: string;
  order: number;
  content: string;
}

interface UpdateModuleForm {
  title: string;
  description: string;
  order: number;
  content: string;
}

interface CreateCapsuleForm {
  title: string;
  description: string;
  videoUrl: string;
  time: number;
  order: number;
  moduleId: string;
}

interface UpdateCapsuleForm {
  title: string;
  description: string;
  videoUrl: string;
  time: number;
  order: number;
  moduleId: string;
}

const AdminCinepro = () => {
  const [modules, setModules] = useState<Module[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // États pour la création de module
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createForm, setCreateForm] = useState<CreateModuleForm>({
    title: "",
    description: "",
    order: 1,
    content: "",
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState("");
  const [createSuccess, setCreateSuccess] = useState(false);

  // États pour l'édition de module
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedModule, setSelectedModule] = useState<Module | null>(null);
  const [editForm, setEditForm] = useState<UpdateModuleForm>({
    title: "",
    description: "",
    order: 1,
    content: "",
  });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState("");
  const [editSuccess, setEditSuccess] = useState(false);

  // États pour la suppression de module
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [moduleToDelete, setModuleToDelete] = useState<Module | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [deleteSuccess, setDeleteSuccess] = useState(false);

  // États pour affichage des capsules
  const [viewingModuleCapsules, setViewingModuleCapsules] =
    useState<Module | null>(null);
  const [capsules, setCapsules] = useState<Capsule[]>([]);
  const [loadingCapsules, setLoadingCapsules] = useState(false);
  const [capsuleError, setCapsuleError] = useState("");

  // États pour la création de capsule
  const [isCreateCapsuleModalOpen, setIsCreateCapsuleModalOpen] =
    useState(false);
  const [createCapsuleForm, setCreateCapsuleForm] = useState<CreateCapsuleForm>(
    {
      title: "",
      description: "",
      videoUrl: "",
      time: 15,
      order: 1,
      moduleId: "",
    }
  );
  const [createCapsuleLoading, setCreateCapsuleLoading] = useState(false);
  const [createCapsuleError, setCreateCapsuleError] = useState("");
  const [createCapsuleSuccess, setCreateCapsuleSuccess] = useState(false);

  // États pour l'édition de capsule
  const [isEditCapsuleModalOpen, setIsEditCapsuleModalOpen] = useState(false);
  const [selectedCapsule, setSelectedCapsule] = useState<Capsule | null>(null);
  const [editCapsuleForm, setEditCapsuleForm] = useState<UpdateCapsuleForm>({
    title: "",
    description: "",
    videoUrl: "",
    time: 0,
    order: 1,
    moduleId: "",
  });
  const [editCapsuleLoading, setEditCapsuleLoading] = useState(false);
  const [editCapsuleError, setEditCapsuleError] = useState("");
  const [editCapsuleSuccess, setEditCapsuleSuccess] = useState(false);

  // États pour la suppression de capsule
  const [isDeleteCapsuleModalOpen, setIsDeleteCapsuleModalOpen] =
    useState(false);
  const [capsuleToDelete, setCapsuleToDelete] = useState<Capsule | null>(null);
  const [deleteCapsuleLoading, setDeleteCapsuleLoading] = useState(false);
  const [deleteCapsuleError, setDeleteCapsuleError] = useState("");
  const [deleteCapsuleSuccess, setDeleteCapsuleSuccess] = useState(false);

  // États pour voir les détails d'une capsule
  const [isViewCapsuleModalOpen, setIsViewCapsuleModalOpen] = useState(false);
  const [capsuleToView, setCapsuleToView] = useState<Capsule | null>(null);

  useEffect(() => {
    fetchModules();
  }, []);

  const fetchModules = async () => {
    setLoading(true);
    setError("");

    try {
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        throw new Error("Vous n'êtes pas connecté");
      }

      const response = await fetch(API_ENDPOINTS.CINEPRO.GET_MODULES, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.message || "Erreur lors de la récupération des modules"
        );
      }

      setModules(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Une erreur est survenue");
      console.error("Erreur de récupération des modules:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    formType: "create" | "edit"
  ) => {
    const { name, value } = e.target;
    const setValue = (prev: CreateModuleForm | UpdateModuleForm) => ({
      ...prev,
      [name]: name === "order" ? Number.parseInt(value) || 0 : value,
    });

    if (formType === "create") {
      setCreateForm((prev) => setValue(prev) as CreateModuleForm);
    } else {
      setEditForm((prev) => setValue(prev) as UpdateModuleForm);
    }
  };

  const handleCreateModule = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateLoading(true);
    setCreateError("");
    setCreateSuccess(false);

    try {
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        throw new Error("Vous n'êtes pas connecté");
      }

      const response = await fetch(API_ENDPOINTS.CINEPRO.CREATE_MODULE, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(createForm),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Erreur lors de la création du module");
      }

      // Rafraîchir la liste des modules
      fetchModules();

      // Réinitialiser le formulaire et afficher un message de succès
      setCreateForm({
        title: "",
        description: "",
        order: 1,
        content: "",
      });

      setCreateSuccess(true);

      // Fermer le modal après 2 secondes
      setTimeout(() => {
        setIsCreateModalOpen(false);
        setCreateSuccess(false);
      }, 2000);
    } catch (err) {
      setCreateError(
        err instanceof Error ? err.message : "Une erreur est survenue"
      );
      console.error("Erreur de création de module:", err);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleEditButtonClick = (module: Module) => {
    setSelectedModule(module);
    setEditForm({
      title: module.title,
      description: module.description,
      order: module.order || 1,
      content: module.content || "",
    });
    setIsEditModalOpen(true);
    setEditError("");
    setEditSuccess(false);
  };

  const handleUpdateModule = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedModule) return;

    setEditLoading(true);
    setEditError("");
    setEditSuccess(false);

    try {
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        throw new Error("Vous n'êtes pas connecté");
      }

      const response = await fetch(
        API_ENDPOINTS.CINEPRO.UPDATE_MODULE(selectedModule.id),
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(editForm),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.message || "Erreur lors de la mise à jour du module"
        );
      }

      // Rafraîchir la liste des modules
      fetchModules();

      setEditSuccess(true);

      // Fermer le modal après 2 secondes
      setTimeout(() => {
        setIsEditModalOpen(false);
        setEditSuccess(false);
        setSelectedModule(null);
      }, 2000);
    } catch (err) {
      setEditError(
        err instanceof Error ? err.message : "Une erreur est survenue"
      );
      console.error("Erreur de mise à jour du module:", err);
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteButtonClick = (module: Module) => {
    setModuleToDelete(module);
    setIsDeleteModalOpen(true);
    setDeleteError("");
    setDeleteSuccess(false);
  };

  const handleDeleteModule = async () => {
    if (!moduleToDelete) return;

    setDeleteLoading(true);
    setDeleteError("");
    setDeleteSuccess(false);

    try {
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        throw new Error("Vous n'êtes pas connecté");
      }

      const response = await fetch(
        API_ENDPOINTS.CINEPRO.DELETE_MODULE(moduleToDelete.id),
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(
          data.message || "Erreur lors de la suppression du module"
        );
      }

      // Rafraîchir la liste des modules
      fetchModules();

      setDeleteSuccess(true);

      // Fermer le modal après 2 secondes
      setTimeout(() => {
        setIsDeleteModalOpen(false);
        setDeleteSuccess(false);
        setModuleToDelete(null);
      }, 2000);
    } catch (err) {
      setDeleteError(
        err instanceof Error ? err.message : "Une erreur est survenue"
      );
      console.error("Erreur de suppression du module:", err);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleViewCapsules = async (module: Module) => {
    setViewingModuleCapsules(module);
    fetchCapsules(module.id);
  };

  const fetchCapsules = async (moduleId: string) => {
    setLoadingCapsules(true);
    setCapsuleError("");

    try {
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        throw new Error("Vous n'êtes pas connecté");
      }

      const response = await fetch(
        API_ENDPOINTS.CINEPRO.GET_MODULE_COURSES(moduleId),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.message || "Erreur lors de la récupération des capsules"
        );
      }

      // Trier les capsules par ordre si disponible
      const sortedCapsules = [...data].sort((a, b) => {
        const orderA = a.order !== undefined ? a.order : 999;
        const orderB = b.order !== undefined ? b.order : 999;
        return orderA - orderB;
      });

      setCapsules(sortedCapsules);
    } catch (err) {
      setCapsuleError(
        err instanceof Error ? err.message : "Une erreur est survenue"
      );
      console.error("Erreur de récupération des capsules:", err);
    } finally {
      setLoadingCapsules(false);
    }
  };

  const handleCapsuleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setCreateCapsuleForm((prev) => ({
      ...prev,
      [name]:
        name === "time" || name === "order" ? Number.parseInt(value) || 0 : value,
    }));
  };

  const handleCreateCapsuleButtonClick = () => {
    if (!viewingModuleCapsules) return;

    // Initialisation simplifiée du formulaire avec valeurs par défaut
    setCreateCapsuleForm({
      title: "",
      description: "",
      videoUrl: "",
      time: 15,
      order: capsules.length + 1,
      moduleId: viewingModuleCapsules.id,
    });

    setIsCreateCapsuleModalOpen(true);
    setCreateCapsuleError("");
    setCreateCapsuleSuccess(false);
  };

  const handleCreateCapsule = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateCapsuleLoading(true);
    setCreateCapsuleError("");
    setCreateCapsuleSuccess(false);

    try {
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        throw new Error("Vous n'êtes pas connecté");
      }

      const response = await fetch(API_ENDPOINTS.CINEPRO.CREATE_CAPSULE, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(createCapsuleForm),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.message || "Erreur lors de la création de la capsule"
        );
      }

      // Rafraîchir la liste des capsules
      if (viewingModuleCapsules) {
        fetchCapsules(viewingModuleCapsules.id);
      }

      setCreateCapsuleSuccess(true);

      // Fermer le modal après 2 secondes
      setTimeout(() => {
        setIsCreateCapsuleModalOpen(false);
        setCreateCapsuleSuccess(false);
      }, 2000);
    } catch (err) {
      setCreateCapsuleError(
        err instanceof Error ? err.message : "Une erreur est survenue"
      );
      console.error("Erreur de création de capsule:", err);
    } finally {
      setCreateCapsuleLoading(false);
    }
  };

  const handleEditCapsuleButtonClick = (capsule: Capsule) => {
    setSelectedCapsule(capsule);

    setEditCapsuleForm({
      title: capsule.title,
      description: capsule.description,
      videoUrl: capsule.videoUrl || "",
      time: capsule.time || 0,
      order: capsule.order || 1,
      moduleId: capsule.moduleId,
    });

    setIsEditCapsuleModalOpen(true);
    setEditCapsuleError("");
    setEditCapsuleSuccess(false);
  };

  const handleEditCapsuleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setEditCapsuleForm((prev) => ({
      ...prev,
      [name]:
        name === "time" || name === "order" ? Number.parseInt(value) || 0 : value,
    }));
  };

  const handleUpdateCapsule = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedCapsule) return;

    setEditCapsuleLoading(true);
    setEditCapsuleError("");
    setEditCapsuleSuccess(false);

    try {
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        throw new Error("Vous n'êtes pas connecté");
      }

      // Utiliser l'endpoint de mise à jour avec l'ID de la capsule
      const response = await fetch(
        API_ENDPOINTS.CINEPRO.GET_CAPSULES(selectedCapsule.id),
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(editCapsuleForm),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.message || "Erreur lors de la mise à jour de la capsule"
        );
      }

      // Rafraîchir la liste des capsules
      if (viewingModuleCapsules) {
        fetchCapsules(viewingModuleCapsules.id);
      }

      setEditCapsuleSuccess(true);

      // Fermer le modal après 2 secondes
      setTimeout(() => {
        setIsEditCapsuleModalOpen(false);
        setEditCapsuleSuccess(false);
      }, 2000);
    } catch (err) {
      setEditCapsuleError(
        err instanceof Error ? err.message : "Une erreur est survenue"
      );
      console.error("Erreur de mise à jour de la capsule:", err);
    } finally {
      setEditCapsuleLoading(false);
    }
  };

  const handleDeleteCapsuleButtonClick = (capsule: Capsule) => {
    setCapsuleToDelete(capsule);
    setIsDeleteCapsuleModalOpen(true);
    setDeleteCapsuleError("");
    setDeleteCapsuleSuccess(false);
  };

  const handleDeleteCapsule = async () => {
    if (!capsuleToDelete) return;

    setDeleteCapsuleLoading(true);
    setDeleteCapsuleError("");
    setDeleteCapsuleSuccess(false);

    try {
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        throw new Error("Vous n'êtes pas connecté");
      }

      // Utiliser l'endpoint de suppression avec l'ID de la capsule
      const response = await fetch(
        API_ENDPOINTS.CINEPRO.GET_CAPSULES(capsuleToDelete.id),
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(
          data.message || "Erreur lors de la suppression de la capsule"
        );
      }

      // Rafraîchir la liste des capsules
      if (viewingModuleCapsules) {
        fetchCapsules(viewingModuleCapsules.id);
      }

      setDeleteCapsuleSuccess(true);

      // Fermer le modal après 2 secondes
      setTimeout(() => {
        setIsDeleteCapsuleModalOpen(false);
        setDeleteCapsuleSuccess(false);
        setCapsuleToDelete(null);
      }, 2000);
    } catch (err) {
      setDeleteCapsuleError(
        err instanceof Error ? err.message : "Une erreur est survenue"
      );
      console.error("Erreur de suppression de la capsule:", err);
    } finally {
      setDeleteCapsuleLoading(false);
    }
  };

  const handleViewCapsuleDetails = (capsule: Capsule) => {
    setCapsuleToView(capsule);
    setIsViewCapsuleModalOpen(true);
  };

  return (
    <div className="p-8">
      <h1 className="text-2xl font-bold">Cinepro</h1>
      <p className="mt-4">Gérez les modules et capsules de la plateforme.</p>

      {!viewingModuleCapsules ? (
        <div className="mt-8 bg-white p-6 rounded-lg shadow">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">Modules</h2>
            <button
              className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors"
              onClick={() => setIsCreateModalOpen(true)}
            >
              Ajouter un module
            </button>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-indigo-600 border-r-transparent"></div>
              <p className="mt-2 text-gray-600">Chargement des modules...</p>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-md">
              {error}
            </div>
          ) : modules.length === 0 ? (
            <p className="text-gray-600 py-4">
              Aucun module disponible pour le moment.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      N°
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Titre
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Description
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Date de création
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {modules.map((module, index) => (
                    <tr key={module.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {index + 1}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {module.title}
                      </td>
                      <td className="px-6 py-4">{module.description}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {new Date(module.createdAt).toLocaleDateString("fr-FR")}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-4">
                          <button
                            className="text-indigo-600 hover:text-indigo-900 p-1 rounded-full hover:bg-indigo-100 transition-colors"
                            onClick={() => handleEditButtonClick(module)}
                            title="Éditer"
                          >
                            <FaEdit size={18} />
                          </button>
                          <button
                            className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-100 transition-colors"
                            onClick={() => handleDeleteButtonClick(module)}
                            title="Supprimer"
                          >
                            <FaTrash size={18} />
                          </button>
                          <button
                            className="text-green-600 hover:text-green-900 p-1 rounded-full hover:bg-green-100 transition-colors"
                            onClick={() => handleViewCapsules(module)}
                            title="Voir les capsules"
                          >
                            <FaEye size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        <div className="mt-8 bg-white p-6 rounded-lg shadow">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center">
              <button
                onClick={() => setViewingModuleCapsules(null)}
                className="mr-3 text-gray-600 hover:text-indigo-600 transition-colors"
                title="Retour aux modules"
              >
                <FaArrowLeft size={16} />
              </button>
              <h2 className="text-xl font-semibold">
                Capsules du module : {viewingModuleCapsules.title}
              </h2>
            </div>
            <button
              className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors flex items-center"
              onClick={handleCreateCapsuleButtonClick}
            >
              <FaPlus className="mr-2" size={14} /> Ajouter une capsule
            </button>
          </div>

          {loadingCapsules ? (
            <div className="text-center py-8">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-indigo-600 border-r-transparent"></div>
              <p className="mt-2 text-gray-600">Chargement des capsules...</p>
            </div>
          ) : capsuleError ? (
            <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-md">
              {capsuleError}
            </div>
          ) : capsules.length === 0 ? (
            <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
              <FaEye size={36} className="mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600">
                Aucune capsule n'est disponible pour ce module.
              </p>
              <p className="text-gray-500 text-sm mt-2">
                Cliquez sur "Ajouter une capsule" pour créer votre première
                capsule.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      N°
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Titre
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Description
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Date de création
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {capsules.map((capsule, index) => (
                    <tr key={capsule.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {index + 1}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {capsule.title}
                      </td>
                      <td className="px-6 py-4">{capsule.description}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {new Date(capsule.createdAt).toLocaleDateString(
                          "fr-FR"
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-4">
                          <button
                            className="text-indigo-600 hover:text-indigo-900 p-1 rounded-full hover:bg-indigo-100 transition-colors"
                            onClick={() =>
                              handleEditCapsuleButtonClick(capsule)
                            }
                            title="Éditer"
                          >
                            <FaEdit size={18} />
                          </button>
                          <button
                            className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-100 transition-colors"
                            onClick={() =>
                              handleDeleteCapsuleButtonClick(capsule)
                            }
                            title="Supprimer"
                          >
                            <FaTrash size={18} />
                          </button>
                          <button
                            className="text-blue-600 hover:text-blue-900 p-1 rounded-full hover:bg-blue-100 transition-colors"
                            onClick={() => handleViewCapsuleDetails(capsule)}
                            title="Voir le détail"
                          >
                            <FaEye size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Modal de création de module */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex justify-between items-center px-6 py-4 border-b">
              <h3 className="text-lg font-semibold">Créer un nouveau module</h3>
              <button
                className="text-gray-500 hover:text-gray-700"
                onClick={() => setIsCreateModalOpen(false)}
              >
                <FaTimes size={18} />
              </button>
            </div>
            <form onSubmit={handleCreateModule} className="p-6">
              {createError && (
                <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded-md mb-4">
                  {createError}
                </div>
              )}
              {createSuccess && (
                <div className="bg-green-50 border border-green-200 text-green-800 p-3 rounded-md mb-4">
                  Module créé avec succès!
                </div>
              )}
              <div className="mb-4">
                <label htmlFor="create-module-title" className="block text-sm font-medium text-gray-700 mb-1">
                  Titre
                </label>
                <input
                  id="create-module-title"
                  type="text"
                  name="title"
                  value={createForm.title}
                  onChange={(e) => handleInputChange(e, "create")}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                />
              </div>
              <div className="mb-4">
                <label htmlFor="create-module-description" className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  id="create-module-description"
                  name="description"
                  value={createForm.description}
                  onChange={(e) => handleInputChange(e, "create")}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  rows={3}
                  required
                />
              </div>
              <div className="mb-4">
                <label htmlFor="create-module-order" className="block text-sm font-medium text-gray-700 mb-1">
                  Ordre
                </label>
                <input
                  id="create-module-order"
                  type="number"
                  name="order"
                  value={createForm.order}
                  onChange={(e) => handleInputChange(e, "create")}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  min="1"
                  required
                />
              </div>
              <div className="mb-4">
                <label htmlFor="create-module-content" className="block text-sm font-medium text-gray-700 mb-1">
                  Contenu
                </label>
                <textarea
                  id="create-module-content"
                  name="content"
                  value={createForm.content}
                  onChange={(e) => handleInputChange(e, "create")}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  rows={5}
                  required
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  onClick={() => setIsCreateModalOpen(false)}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors disabled:bg-indigo-400"
                  disabled={createLoading}
                >
                  {createLoading ? (
                    <span className="flex items-center gap-2">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-r-transparent"></div>
                      Création...
                    </span>
                  ) : (
                    "Créer"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal d'édition de module */}
      {isEditModalOpen && selectedModule && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex justify-between items-center px-6 py-4 border-b">
              <h3 className="text-lg font-semibold">Modifier le module</h3>
              <button
                className="text-gray-500 hover:text-gray-700"
                onClick={() => setIsEditModalOpen(false)}
              >
                <FaTimes size={18} />
              </button>
            </div>
            <form onSubmit={handleUpdateModule} className="p-6">
              {editError && (
                <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded-md mb-4">
                  {editError}
                </div>
              )}
              {editSuccess && (
                <div className="bg-green-50 border border-green-200 text-green-800 p-3 rounded-md mb-4">
                  Module mis à jour avec succès!
                </div>
              )}
              <div className="mb-4">
                <label htmlFor="edit-module-title" className="block text-sm font-medium text-gray-700 mb-1">
                  Titre
                </label>
                <input
                  id="edit-module-title"
                  type="text"
                  name="title"
                  value={editForm.title}
                  onChange={(e) => handleInputChange(e, "edit")}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                />
              </div>
              <div className="mb-4">
                <label htmlFor="edit-module-description" className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  id="edit-module-description"
                  name="description"
                  value={editForm.description}
                  onChange={(e) => handleInputChange(e, "edit")}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  rows={3}
                  required
                />
              </div>
              <div className="mb-4">
                <label htmlFor="edit-module-order" className="block text-sm font-medium text-gray-700 mb-1">
                  Ordre
                </label>
                <input
                  id="edit-module-order"
                  type="number"
                  name="order"
                  value={editForm.order}
                  onChange={(e) => handleInputChange(e, "edit")}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  min="1"
                  required
                />
              </div>
              <div className="mb-4">
                <label htmlFor="edit-module-content" className="block text-sm font-medium text-gray-700 mb-1">
                  Contenu
                </label>
                <textarea
                  id="edit-module-content"
                  name="content"
                  value={editForm.content}
                  onChange={(e) => handleInputChange(e, "edit")}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  rows={5}
                  required
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  onClick={() => setIsEditModalOpen(false)}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors disabled:bg-indigo-400"
                  disabled={editLoading}
                >
                  {editLoading ? (
                    <span className="flex items-center gap-2">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-r-transparent"></div>
                      Mise à jour...
                    </span>
                  ) : (
                    "Mettre à jour"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de suppression de module */}
      {isDeleteModalOpen && moduleToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex justify-between items-center px-6 py-4 border-b">
              <h3 className="text-lg font-semibold flex items-center">
                <FaExclamationTriangle
                  className="text-yellow-500 mr-2"
                  size={20}
                />
                Confirmation de suppression
              </h3>
              <button
                className="text-gray-500 hover:text-gray-700"
                onClick={() => setIsDeleteModalOpen(false)}
              >
                <FaTimes size={18} />
              </button>
            </div>
            <div className="p-6">
              {deleteError && (
                <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded-md mb-4">
                  {deleteError}
                </div>
              )}
              {deleteSuccess && (
                <div className="bg-green-50 border border-green-200 text-green-800 p-3 rounded-md mb-4">
                  Module supprimé avec succès!
                </div>
              )}
              <p className="mb-4">
                Êtes-vous sûr de vouloir supprimer le module{" "}
                <span className="font-semibold">{moduleToDelete.title}</span> ?
              </p>
              <p className="mb-6 text-sm text-gray-600">
                Cette action est irréversible et supprimera également toutes les
                capsules associées à ce module.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  onClick={() => setIsDeleteModalOpen(false)}
                  disabled={deleteLoading}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:bg-red-400"
                  onClick={handleDeleteModule}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? (
                    <span className="flex items-center gap-2">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-r-transparent"></div>
                      Suppression...
                    </span>
                  ) : (
                    "Supprimer"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de création de capsule */}
      {isCreateCapsuleModalOpen && viewingModuleCapsules && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex justify-between items-center px-6 py-4 border-b">
              <h3 className="text-lg font-semibold flex items-center">
                <FaVideo className="text-indigo-500 mr-2" size={18} />
                Créer une nouvelle capsule
              </h3>
              <button
                className="text-gray-500 hover:text-gray-700"
                onClick={() => setIsCreateCapsuleModalOpen(false)}
              >
                <FaTimes size={18} />
              </button>
            </div>
            <form onSubmit={handleCreateCapsule} className="p-6">
              {createCapsuleError && (
                <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded-md mb-4">
                  {createCapsuleError}
                </div>
              )}
              {createCapsuleSuccess && (
                <div className="bg-green-50 border border-green-200 text-green-800 p-3 rounded-md mb-4">
                  Capsule créée avec succès!
                </div>
              )}
              <div className="mb-4">
                <label htmlFor="create-capsule-title" className="block text-sm font-medium text-gray-700 mb-1">
                  Titre
                </label>
                <input
                  id="create-capsule-title"
                  type="text"
                  name="title"
                  value={createCapsuleForm.title}
                  onChange={handleCapsuleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                />
              </div>
              <div className="mb-4">
                <label htmlFor="create-capsule-description" className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  id="create-capsule-description"
                  name="description"
                  value={createCapsuleForm.description}
                  onChange={handleCapsuleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  rows={3}
                  required
                />
              </div>
              <div className="mb-4">
                <label htmlFor="create-capsule-videoUrl" className="block text-sm font-medium text-gray-700 mb-1">
                  URL de la vidéo
                </label>
                <input
                  id="create-capsule-videoUrl"
                  type="url"
                  name="videoUrl"
                  value={createCapsuleForm.videoUrl}
                  onChange={handleCapsuleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="https://example.com/video.mp4"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="mb-4">
                  <label htmlFor="create-capsule-time" className="block text-sm font-medium text-gray-700 mb-1">
                    Durée (minutes)
                  </label>
                  <input
                    id="create-capsule-time"
                    type="number"
                    name="time"
                    value={createCapsuleForm.time}
                    onChange={handleCapsuleInputChange}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    min="0"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label htmlFor="create-capsule-order" className="block text-sm font-medium text-gray-700 mb-1">
                    Ordre
                  </label>
                  <input
                    id="create-capsule-order"
                    type="number"
                    name="order"
                    value={createCapsuleForm.order}
                    onChange={handleCapsuleInputChange}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    min="1"
                    required
                  />
                </div>
              </div>
              <div className="mb-4">
                <label htmlFor="create-capsule-module" className="block text-sm font-medium text-gray-700 mb-1">
                  Module
                </label>
                <input
                  id="create-capsule-module"
                  type="text"
                  value={viewingModuleCapsules.title}
                  className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md text-gray-600"
                  disabled
                />
                <input
                  type="hidden"
                  name="moduleId"
                  value={createCapsuleForm.moduleId}
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  onClick={() => setIsCreateCapsuleModalOpen(false)}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors disabled:bg-indigo-400"
                  disabled={createCapsuleLoading}
                >
                  {createCapsuleLoading ? (
                    <span className="flex items-center gap-2">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-r-transparent"></div>
                      Création...
                    </span>
                  ) : (
                    "Créer"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal d'édition de capsule */}
      {isEditCapsuleModalOpen && selectedCapsule && viewingModuleCapsules && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex justify-between items-center px-6 py-4 border-b">
              <h3 className="text-lg font-semibold flex items-center">
                <FaVideo className="text-indigo-500 mr-2" size={18} />
                Modifier la capsule
              </h3>
              <button
                className="text-gray-500 hover:text-gray-700"
                onClick={() => setIsEditCapsuleModalOpen(false)}
              >
                <FaTimes size={18} />
              </button>
            </div>
            <form onSubmit={handleUpdateCapsule} className="p-6">
              {editCapsuleError && (
                <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded-md mb-4">
                  {editCapsuleError}
                </div>
              )}
              {editCapsuleSuccess && (
                <div className="bg-green-50 border border-green-200 text-green-800 p-3 rounded-md mb-4">
                  Capsule mise à jour avec succès!
                </div>
              )}
              <div className="mb-4">
                <label htmlFor="edit-capsule-title" className="block text-sm font-medium text-gray-700 mb-1">
                  Titre
                </label>
                <input
                  id="edit-capsule-title"
                  type="text"
                  name="title"
                  value={editCapsuleForm.title}
                  onChange={handleEditCapsuleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                />
              </div>
              <div className="mb-4">
                <label htmlFor="edit-capsule-description" className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  id="edit-capsule-description"
                  name="description"
                  value={editCapsuleForm.description}
                  onChange={handleEditCapsuleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  rows={3}
                  required
                />
              </div>
              <div className="mb-4">
                <label htmlFor="edit-capsule-videoUrl" className="block text-sm font-medium text-gray-700 mb-1">
                  URL de la vidéo
                </label>
                <input
                  id="edit-capsule-videoUrl"
                  type="url"
                  name="videoUrl"
                  value={editCapsuleForm.videoUrl}
                  onChange={handleEditCapsuleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="https://example.com/video.mp4"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="mb-4">
                  <label htmlFor="edit-capsule-time" className="block text-sm font-medium text-gray-700 mb-1">
                    Durée (minutes)
                  </label>
                  <input
                    id="edit-capsule-time"
                    type="number"
                    name="time"
                    value={editCapsuleForm.time}
                    onChange={handleEditCapsuleInputChange}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    min="0"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label htmlFor="edit-capsule-order" className="block text-sm font-medium text-gray-700 mb-1">
                    Ordre
                  </label>
                  <input
                    id="edit-capsule-order"
                    type="number"
                    name="order"
                    value={editCapsuleForm.order}
                    onChange={handleEditCapsuleInputChange}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    min="1"
                    required
                  />
                </div>
              </div>
              <div className="mb-4">
                <label htmlFor="edit-capsule-module" className="block text-sm font-medium text-gray-700 mb-1">
                  Module
                </label>
                <input
                  id="edit-capsule-module"
                  type="text"
                  value={viewingModuleCapsules.title}
                  className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md text-gray-600"
                  disabled
                />
                <input
                  type="hidden"
                  name="moduleId"
                  value={editCapsuleForm.moduleId}
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  onClick={() => setIsEditCapsuleModalOpen(false)}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors disabled:bg-indigo-400"
                  disabled={editCapsuleLoading}
                >
                  {editCapsuleLoading ? (
                    <span className="flex items-center gap-2">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-r-transparent"></div>
                      Mise à jour...
                    </span>
                  ) : (
                    "Mettre à jour"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de suppression de capsule */}
      {isDeleteCapsuleModalOpen && capsuleToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex justify-between items-center px-6 py-4 border-b">
              <h3 className="text-lg font-semibold flex items-center">
                <FaExclamationTriangle
                  className="text-yellow-500 mr-2"
                  size={20}
                />
                Confirmation de suppression
              </h3>
              <button
                className="text-gray-500 hover:text-gray-700"
                onClick={() => setIsDeleteCapsuleModalOpen(false)}
              >
                <FaTimes size={18} />
              </button>
            </div>
            <div className="p-6">
              {deleteCapsuleError && (
                <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded-md mb-4">
                  {deleteCapsuleError}
                </div>
              )}
              {deleteCapsuleSuccess && (
                <div className="bg-green-50 border border-green-200 text-green-800 p-3 rounded-md mb-4">
                  Capsule supprimée avec succès!
                </div>
              )}
              <p className="mb-4">
                Êtes-vous sûr de vouloir supprimer la capsule{" "}
                <span className="font-semibold">{capsuleToDelete.title}</span> ?
              </p>
              <p className="mb-6 text-sm text-gray-600">
                Cette action est irréversible et supprimera la capsule.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  onClick={() => setIsDeleteCapsuleModalOpen(false)}
                  disabled={deleteCapsuleLoading}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:bg-red-400"
                  onClick={handleDeleteCapsule}
                  disabled={deleteCapsuleLoading}
                >
                  {deleteCapsuleLoading ? (
                    <span className="flex items-center gap-2">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-r-transparent"></div>
                      Suppression...
                    </span>
                  ) : (
                    "Supprimer"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de détails de capsule */}
      {isViewCapsuleModalOpen && capsuleToView && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl">
            <div className="flex justify-between items-center px-6 py-4 border-b">
              <h3 className="text-lg font-semibold flex items-center">
                <FaVideo className="text-blue-500 mr-2" size={18} />
                Détails de la capsule
              </h3>
              <button
                className="text-gray-500 hover:text-gray-700"
                onClick={() => setIsViewCapsuleModalOpen(false)}
              >
                <FaTimes size={18} />
              </button>
            </div>
            <div className="p-6 max-h-[80vh] overflow-y-auto">
              <div className="mb-4">
                <h4 className="text-xl font-medium text-gray-800 mb-1">
                  {capsuleToView.title}
                </h4>
                <p className="text-sm text-gray-500">
                  Créée le{" "}
                  {new Date(capsuleToView.createdAt).toLocaleDateString(
                    "fr-FR"
                  )}
                </p>
              </div>

              {capsuleToView.videoUrl && (
                <div className="mb-6">
                  <div className="aspect-w-16 aspect-h-9 bg-black rounded-lg overflow-hidden mb-2">
                    {capsuleToView.videoUrl.includes("youtube.com") ||
                    capsuleToView.videoUrl.includes("youtu.be") ? (
                      <iframe
                        src={
                          capsuleToView.videoUrl.includes("watch?v=")
                            ? capsuleToView.videoUrl.replace(
                                "watch?v=",
                                "embed/"
                              )
                            : capsuleToView.videoUrl.replace(
                                "youtu.be/",
                                "youtube.com/embed/"
                              )
                        }
                        title={`Vidéo: ${capsuleToView.title}`}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="w-full h-full rounded-lg"
                      ></iframe>
                    ) : capsuleToView.videoUrl.includes("vimeo.com") ? (
                      <iframe
                        src={capsuleToView.videoUrl.replace(
                          "vimeo.com/",
                          "player.vimeo.com/video/"
                        )}
                        title={`Vidéo: ${capsuleToView.title}`}
                        allow="autoplay; fullscreen; picture-in-picture"
                        allowFullScreen
                        className="w-full h-full rounded-lg"
                      ></iframe>
                    ) : (
                      <video
                        src={capsuleToView.videoUrl}
                        controls
                        className="w-full h-full rounded-lg"
                        controlsList="nodownload"
                        title={`Vidéo: ${capsuleToView.title}`}
                      >
                        <track kind="captions" />
                        Votre navigateur ne prend pas en charge la lecture de
                        vidéos HTML5.
                      </video>
                    )}
                  </div>
                  <div className="flex items-center bg-gray-50 p-3 rounded-md">
                    <span className="text-sm text-gray-600 font-medium">
                      Source de la vidéo:
                    </span>
                    <span className="text-gray-600 text-sm ml-2 truncate flex-1">
                      {capsuleToView.videoUrl}
                    </span>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <h5 className="text-sm font-medium text-gray-700 mb-2">
                    Description
                  </h5>
                  <div className="text-gray-600 bg-gray-50 p-3 rounded-md min-h-[100px]">
                    {capsuleToView.description ||
                      "Aucune description disponible"}
                  </div>
                </div>

                <div>
                  <h5 className="text-sm font-medium text-gray-700 mb-2">
                    Informations
                  </h5>
                  <div className="bg-gray-50 p-3 rounded-md space-y-2">
                    <div>
                      <span className="text-sm font-medium text-gray-700">
                        ID de la capsule:
                      </span>
                      <p className="text-gray-600 text-sm mt-1 break-all">
                        {capsuleToView.id}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-700">
                        ID du module:
                      </span>
                      <p className="text-gray-600 text-sm mt-1 break-all">
                        {capsuleToView.moduleId}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {capsuleToView.content && (
                <div className="mb-6">
                  <h5 className="text-sm font-medium text-gray-700 mb-2">
                    Contenu
                  </h5>
                  <div className="bg-gray-50 p-3 rounded-md max-h-48 overflow-y-auto">
                    <p className="text-gray-600 whitespace-pre-wrap text-sm">
                      {capsuleToView.content}
                    </p>
                  </div>
                </div>
              )}

              <div className="flex justify-end pt-4">
                <button
                  type="button"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  onClick={() => setIsViewCapsuleModalOpen(false)}
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCinepro;
